/**
 * Helper function to get text content from an element, handling namespaces
 */
const getElementText = (parent, tagName) => {
  // Try direct child first
  const element = parent.querySelector(tagName);
  if (element) return element.textContent?.trim();

  // Try with namespace prefix
  const allChildren = parent.children || parent.childNodes;
  for (let i = 0; i < allChildren.length; i++) {
    const child = allChildren[i];
    const localName = child.localName || child.tagName?.split(':').pop();
    if (localName === tagName) {
      return child.textContent?.trim();
    }
  }
  return null;
};

/**
 * Parses a GPX file and extracts track points with all available data
 * @param {string|File} gpxData - GPX file content as string or File object
 * @returns {Promise<Array<{lat: number, lng: number, elevation?: number, time?: string, [key: string]: any}>>} Array of track points with data
 */
export const parseGPX = async (gpxData) => {
  let xmlText;

  // Handle File object
  if (gpxData instanceof File) {
    xmlText = await gpxData.text();
  } else {
    xmlText = gpxData;
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

  // Check for parsing errors
  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('Invalid GPX file format');
  }

  const trackPoints = [];

  // Try to find the GPX namespace
  const gpxNamespace = 'http://www.topografix.com/GPX/1/1';
  let points = [];

  // First, try to get elements with namespace (works for properly namespaced GPX)
  try {
    const nsPoints = xmlDoc.getElementsByTagNameNS(gpxNamespace, 'trkpt');
    if (nsPoints.length > 0) {
      points = nsPoints;
    }
  } catch (e) {
    // If namespace method fails, continue to other methods
  }

  // If no points found with namespace, try without namespace (for non-namespaced GPX files)
  if (points.length === 0) {
    const noNsPoints = xmlDoc.getElementsByTagName('trkpt');
    if (noNsPoints.length > 0) {
      points = noNsPoints;
    }
  }

  // If still no points, try to find any element with local name 'trkpt' (for prefixed namespaces like ns0:trkpt)
  if (points.length === 0) {
    const allElements = xmlDoc.getElementsByTagName('*');
    const foundPoints = [];
    for (let i = 0; i < allElements.length; i++) {
      const element = allElements[i];
      const tagName = element.tagName || '';
      const localName = element.localName || tagName.split(':').pop() || tagName;

      if (localName === 'trkpt' || tagName.endsWith(':trkpt') || tagName === 'trkpt') {
        foundPoints.push(element);
      }
    }
    if (foundPoints.length > 0) {
      points = foundPoints;
    }
  }

  // Extract data from track points
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const lat = parseFloat(point.getAttribute('lat'));
    const lon = parseFloat(point.getAttribute('lon'));

    if (!isNaN(lat) && !isNaN(lon)) {
      const pointData = {
        lat,
        lng: lon,
        index: i,
      };

      // Extract elevation
      const elevation = getElementText(point, 'ele');
      if (elevation) {
        pointData.elevation = parseFloat(elevation);
      }

      // Extract time
      const time = getElementText(point, 'time');
      if (time) {
        pointData.time = time;
      }

      // Extract any other child elements
      const children = point.children || point.childNodes;
      for (let j = 0; j < children.length; j++) {
        const child = children[j];
        if (child.nodeType === 1) {
          // Element node
          const localName = child.localName || child.tagName?.split(':').pop();
          if (localName && localName !== 'ele' && localName !== 'time') {
            pointData[localName] = child.textContent?.trim() || '';
          }
        }
      }

      trackPoints.push(pointData);
    }
  }

  if (trackPoints.length === 0) {
    throw new Error('No valid track points found in GPX file');
  }

  return trackPoints;
};
